'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { JumpLinkSection } from './types';

const PAGE_HEADER_SELECTOR = '#TopNav-mainNav';
const DESKTOP_NAV_SELECTOR = '.Navigation-lg';
const MOBILE_NAV_SELECTOR = '.Navigation-sm';
const SCROLL_END_DELAY = 150;
const SECTION_ACTIVATION_TOLERANCE = 2;
const SCROLL_EXTRA_OFFSET = 24;
// `md` breakpoint (tailwind/global.css `--breakpoint-md`). The legacy component
// hid its left/right scroll arrows below this width (`getBreakpoint() === 'small'`).
const TABLET_MEDIA_QUERY = '(min-width: 701px)';
// Ignore sub-pixel rounding when deciding whether the tab strip can still scroll.
const SCROLL_EPSILON = 1;

type SectionElement = {
	section: JumpLinkSection;
	element: HTMLElement;
};

export default function useJumpLinkNav({ sections }: { sections: JumpLinkSection[] }) {
	const placeholderRef = useRef<HTMLDivElement | null>(null);
	const navRef = useRef<HTMLElement | null>(null);
	const tabListRef = useRef<HTMLUListElement | null>(null);
	const scrollTargetKeyRef = useRef<string | null>(null);
	const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const frameRef = useRef<number | null>(null);
	const [activeSectionKey, setActiveSectionKey] = useState<string | null>(sections[0]?.key ?? null);
	const [isSticky, setIsSticky] = useState(false);
	const [isVisible, setIsVisible] = useState(true);
	const [navHeight, setNavHeight] = useState(0);
	const [pageHeaderBottom, setPageHeaderBottom] = useState(0);
	const [isTabletUp, setIsTabletUp] = useState(false);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	useEffect(() => {
		const nav = navRef.current;
		if (!nav) return;

		const updateNavHeight = () => setNavHeight(nav.getBoundingClientRect().height);
		updateNavHeight();

		if (typeof ResizeObserver === 'undefined') return;

		const observer = new ResizeObserver(updateNavHeight);
		observer.observe(nav);

		return () => observer.disconnect();
	}, []);

	// Track whether we're at or above the tablet breakpoint — the scroll arrows
	// are a pointer affordance and stay hidden on phones (legacy parity).
	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

		const query = window.matchMedia(TABLET_MEDIA_QUERY);
		const update = () => setIsTabletUp(query.matches);
		update();
		query.addEventListener('change', update);

		return () => query.removeEventListener('change', update);
	}, []);

	useEffect(() => {
		const pageHeader = document.querySelector<HTMLElement>(PAGE_HEADER_SELECTOR);
		const desktopNav = pageHeader?.querySelector<HTMLElement>(DESKTOP_NAV_SELECTOR) ?? null;
		const mobileNav = pageHeader?.querySelector<HTMLElement>(MOBILE_NAV_SELECTOR) ?? null;
		const sectionElements = sections.reduce<SectionElement[]>((result, section) => {
			const element = document.getElementById(section.id);
			if (element) result.push({ section, element });
			return result;
		}, []);

		const updateNavigation = () => {
			frameRef.current = null;
			const desktopNavStyle = desktopNav ? window.getComputedStyle(desktopNav) : null;
			const isDesktopNavVisible =
				!!desktopNav &&
				desktopNavStyle?.display !== 'none' &&
				desktopNavStyle?.visibility !== 'hidden';
			const effectiveHeader = isDesktopNavVisible ? desktopNav : (mobileNav ?? pageHeader);
			const headerBottom = Math.max(0, effectiveHeader?.getBoundingClientRect().bottom ?? 0);
			setPageHeaderBottom(headerBottom);

			const placeholder = placeholderRef.current;
			if (!placeholder || !sectionElements.length) {
				setIsSticky(false);
				setIsVisible(true);
				return;
			}

			const lastRect = sectionElements[sectionElements.length - 1].element.getBoundingClientRect();
			const nextIsVisible = lastRect.bottom > headerBottom + navHeight;
			const nextIsSticky = nextIsVisible && placeholder.getBoundingClientRect().top <= headerBottom;
			setIsVisible(nextIsVisible);
			setIsSticky(nextIsSticky);

			if (!nextIsVisible) return;
			if (scrollTargetKeyRef.current) {
				setActiveSectionKey(scrollTargetKeyRef.current);
				return;
			}

			const activationPoint = headerBottom + navHeight + SCROLL_EXTRA_OFFSET;
			const activeSection = sectionElements.reduce<SectionElement>(
				(active, candidate) =>
					candidate.element.getBoundingClientRect().top <=
					activationPoint + SECTION_ACTIVATION_TOLERANCE
						? candidate
						: active,
				sectionElements[0]
			);
			setActiveSectionKey(activeSection.section.key);
		};

		const scheduleUpdate = () => {
			if (frameRef.current === null)
				frameRef.current = window.requestAnimationFrame(updateNavigation);
		};

		const handleScroll = () => {
			scheduleUpdate();

			if (!scrollTargetKeyRef.current) return;
			if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
			scrollEndTimerRef.current = setTimeout(() => {
				scrollTargetKeyRef.current = null;
				updateNavigation();
			}, SCROLL_END_DELAY);
		};

		updateNavigation();
		window.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('resize', scheduleUpdate);

		// The legacy component observed the global header directly so the sticky
		// offset stayed correct when the header changed height without a scroll or
		// window resize (e.g. a dismissible promo bar, shrink-on-scroll header).
		const headerObserver =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate);
		[pageHeader, desktopNav, mobileNav].forEach(element => {
			if (element) headerObserver?.observe(element);
		});

		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', scheduleUpdate);
			headerObserver?.disconnect();
			if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
			if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
		};
	}, [navHeight, sections]);

	// Keep `scroll-margin-top` on the linked sections in sync with the sticky
	// offset so *native* hash navigation (opening `/…#section`, browser
	// back/forward to a hash, a plain anchor jump) lands below the fixed header —
	// not just clicks routed through `scrollTo`. Legacy set this on every card.
	useEffect(() => {
		const offset = Math.max(0, pageHeaderBottom + navHeight + SCROLL_EXTRA_OFFSET);
		const touched: { element: HTMLElement; previous: string }[] = [];

		sections.forEach(section => {
			const element = document.getElementById(section.id);
			if (!element) return;
			touched.push({ element, previous: element.style.scrollMarginTop });
			element.style.scrollMarginTop = `${offset}px`;
		});

		return () => {
			touched.forEach(({ element, previous }) => {
				element.style.scrollMarginTop = previous;
			});
		};
	}, [sections, pageHeaderBottom, navHeight]);

	// Scroll the active tab into view within the horizontal strip.
	useEffect(() => {
		if (!activeSectionKey || !navRef.current) return;
		const tabsContainer = tabListRef.current;
		const activeLink = Array.from(
			(tabsContainer ?? navRef.current).querySelectorAll<HTMLElement>('[data-section-key]')
		).find(link => link.dataset['sectionKey'] === activeSectionKey);
		if (!tabsContainer || !activeLink) return;

		const containerRect = tabsContainer.getBoundingClientRect();
		const activeLinkRect = activeLink.getBoundingClientRect();
		const targetLeft =
			tabsContainer.scrollLeft +
			activeLinkRect.left -
			containerRect.left -
			(tabsContainer.clientWidth - activeLinkRect.width) / 2;

		tabsContainer.scrollTo({ left: targetLeft, behavior: 'smooth' });
	}, [activeSectionKey]);

	// Whether the tab strip is currently clipped on either side — drives arrow
	// visibility. Kept fresh on the strip's own scroll, on resize, and whenever
	// the section list changes.
	const updateScrollAffordances = useCallback(() => {
		const list = tabListRef.current;
		if (!list) {
			setCanScrollLeft(false);
			setCanScrollRight(false);
			return;
		}
		const maxScrollLeft = list.scrollWidth - list.clientWidth;
		setCanScrollLeft(list.scrollLeft > SCROLL_EPSILON);
		setCanScrollRight(list.scrollLeft < maxScrollLeft - SCROLL_EPSILON);
	}, []);

	useEffect(() => {
		const list = tabListRef.current;
		if (!list) return;

		updateScrollAffordances();
		const handleListScroll = () => updateScrollAffordances();
		list.addEventListener('scroll', handleListScroll, { passive: true });
		window.addEventListener('resize', updateScrollAffordances);

		const listObserver =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollAffordances);
		listObserver?.observe(list);

		return () => {
			list.removeEventListener('scroll', handleListScroll);
			window.removeEventListener('resize', updateScrollAffordances);
			listObserver?.disconnect();
		};
	}, [sections, updateScrollAffordances]);

	// Nudge the strip one "clipped tab" at a time, mirroring the legacy arrows.
	const scrollTabs = useCallback((direction: 'left' | 'right') => {
		const list = tabListRef.current;
		if (!list) return;

		const links = Array.from(list.querySelectorAll<HTMLElement>('[data-section-key]'));
		const listRect = list.getBoundingClientRect();
		let delta = 0;

		if (direction === 'left') {
			// Last tab still clipped past the left edge — bring it fully into view.
			for (const link of links) {
				const rect = link.getBoundingClientRect();
				if (rect.left < listRect.left - SCROLL_EPSILON) delta = rect.left - listRect.left;
			}
		} else {
			// First tab clipped past the right edge.
			for (const link of links) {
				const rect = link.getBoundingClientRect();
				if (rect.right > listRect.right + SCROLL_EPSILON) {
					delta = rect.right - listRect.right;
					break;
				}
			}
		}

		if (delta) list.scrollBy({ left: delta, behavior: 'smooth' });
	}, []);

	const scrollTo = useCallback(
		(section: JumpLinkSection, event: React.MouseEvent<HTMLAnchorElement>) => {
			event.preventDefault();
			const element = document.getElementById(section.id);
			if (!element) return;

			scrollTargetKeyRef.current = section.key;
			const targetTop =
				element.getBoundingClientRect().top +
				window.scrollY -
				pageHeaderBottom -
				navHeight -
				SCROLL_EXTRA_OFFSET;
			window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
			setActiveSectionKey(section.key);
			setIsVisible(true);
		},
		[navHeight, pageHeaderBottom]
	);

	return {
		activeSectionKey,
		canScrollLeft: canScrollLeft && isTabletUp,
		canScrollRight: canScrollRight && isTabletUp,
		isSticky,
		isVisible,
		navRef,
		pageHeaderBottom,
		placeholderRef,
		scrollTabs,
		scrollTo,
		tabListRef,
	};
}
